// Компактная карточка блока библиотеки. Переиспользуется таймлайном (см. CONTRACT.md).
import { useState } from 'react';
import type { Segment } from '../../types';
import { formatMoney, segmentAccent, segmentSubtitle, segmentTitle } from '../../core';
import { newId, useActions, useStore } from '../../store';
import { useT } from '../../i18n';
import { KindIcon } from './KindIcon';
import { CloneIcon, DeleteIcon, EditIcon } from './ActionIcons';
import { AddToVariantButton } from './AddToVariantButton';
import { ConfirmDialog } from '../forms';
import { PriceBadge, PriceHistoryModal, PriceRefreshButton } from '../../prices';
import { SEGMENT_DND_MIME } from './constants';
import styles from './SegmentCard.module.css';

export interface SegmentCardProps {
  segment: Segment;
  draggable?: boolean;
  onEdit?(id: string): void;
  /** Блок добавлен недавно и ещё не отмечен просмотренным — лейбл + подсветка.
   *  По умолчанию false: таймлайн переиспользует эту же карточку и не должен
   *  внезапно обрасти анимацией и рамкой из-за смены пропа по умолчанию. */
  isNew?: boolean;
  /** Пользователь дотронулся до новой карточки — её пора считать прочитанной.
   *  Библиотека передаёт это только новым карточкам. */
  onRead?(id: string): void;
}

const ACCENT_VAR: Record<string, string> = {
  flight: 'var(--flight)',
  transfer: 'var(--transfer)',
  lodging: 'var(--lodging)',
  cost: 'var(--cost)',
};

function segmentPrice(segment: Segment) {
  return segment.kind === 'lodging' ? segment.pricePerNight : segment.price;
}

/** Префиксы id — те же, что раздают формы создания (см. src/ui/forms). */
const ID_PREFIX: Partial<Record<Segment['kind'], string>> = {
  flight: 'f',
  transfer: 't',
  lodging: 'l',
  baggage: 'b',
};

/**
 * Копия блока с новым id. Глубокая: деньги и прочие вложенные объекты не должны
 * делиться между оригиналом и клоном. Пара туда-обратно не клонируется —
 * linkedTo у копии сбрасывается, иначе она ссылалась бы на чужого партнёра,
 * который о ней не знает.
 */
function cloneSegment(segment: Segment): Segment {
  const copy: Segment = structuredClone(segment);
  copy.id = newId(ID_PREFIX[segment.kind] ?? segment.kind[0]);
  if (copy.kind === 'flight') copy.linkedTo = undefined;
  return copy;
}

export function SegmentCard({
  segment,
  draggable,
  onEdit,
  isNew = false,
  onRead,
}: SegmentCardProps) {
  const state = useStore();
  const actions = useActions();
  const t = useT();
  const [dragging, setDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Журнал проверок цены: открывается бейджем и закрывается сбросом id.
  const [historyOpen, setHistoryOpen] = useState(false);

  const accent = segmentAccent(segment.kind);
  const price = segmentPrice(segment);
  const converted = price.currency !== state.displayCurrency;

  const affectedVariants = state.variants.filter((v) => v.segmentIds.includes(segment.id)).length;
  const confirmMessage =
    affectedVariants > 0
      ? t('library.delete.used', { count: affectedVariants })
      : t('library.delete.unused');

  // Блок внутри заблокированного варианта правке и удалению не подлежит: иначе
  // замок обходился бы через библиотеку. Тот же гард стоит в reducer.
  const lockedByVariant = state.variants.some(
    (v) => v.locked === true && v.segmentIds.includes(segment.id),
  );
  const lockedTitle = t('library.locked');

  function handleEdit() {
    if (lockedByVariant) return;
    onEdit?.(segment.id);
  }

  /** Ловим на погружении: вложенные кнопки гасят всплытие, а «прочитано» —
   *  про любое касание карточки, включая «Изменить» и «Добавить в…». */
  function handleClickCapture() {
    onRead?.(segment.id);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Только нажатия по самой карточке: иначе Enter на вложенных кнопках
    // («Добавить в…», «Изменить», «Удалить») и на пунктах их меню отменялся бы
    // здешним preventDefault, и клавиатурный путь добавления не работал бы.
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRead?.(segment.id);
      handleEdit();
    }
  }

  function handleDragStart(e: React.DragEvent<HTMLDivElement>) {
    e.dataTransfer.setData(SEGMENT_DND_MIME, segment.id);
    e.dataTransfer.setData('text/plain', segment.id);
    e.dataTransfer.effectAllowed = 'copy';
    setDragging(true);
  }

  function handleDragEnd() {
    setDragging(false);
  }

  return (
    <>
      <div
        className={`${styles.card} ${dragging ? styles.dragging : ''} ${isNew ? styles.isNew : ''}`}
        style={{ '--accent-color': ACCENT_VAR[accent] } as React.CSSProperties}
        role="button"
        tabIndex={0}
        data-segment-id={segment.id}
        draggable={draggable}
        onDragStart={draggable ? handleDragStart : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
        onClickCapture={onRead ? handleClickCapture : undefined}
        onClick={handleEdit}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.row1}>
          <span className={styles.titleWrap}>
            <KindIcon kind={segment.kind} className={styles.icon} />
            <span className={`${styles.title} ${segment.kind === 'flight' ? 'iata' : ''}`}>
              {segmentTitle(segment, t)}
            </span>
            {isNew && (
              <span className={styles.newBadge} title={t('library.new.title')} aria-label={t('library.new.title')}>
                {t('library.new')}
              </span>
            )}
          </span>
          <span className={`${styles.price} mono`}>{formatMoney(price.amount, price.currency, state.currencies)}</span>
        </div>
        <div className={styles.subtitle}>{segmentSubtitle(segment, state.currencies, t)}</div>
        {/* Что показывает агрегатор сейчас — только у рейсов, и только своей
            строкой. Клик по карточке открывает правку, поэтому бейджу нужен
            собственный слой: тот же приём, что у «Добавить в…» ниже. */}
        {segment.kind === 'flight' && (
          <div className={styles.priceLine} onClick={(e) => e.stopPropagation()}>
            <PriceBadge segment={segment} onOpenHistory={() => setHistoryOpen(true)} />
          </div>
        )}
        <div className={styles.metaRow}>
          {converted ? <span className={styles.badge}>{t('library.converted')}</span> : <span />}
          {/* Ряд одних иконок: подписи «Изменить»/«Клонировать»/«Удалить» не
              умещались в узкую карточку и обрезались многоточием, а с приходом
              проверки цены ряд стал ещё и разношёрстным — текст вперемешку с
              иконкой. Имя каждой кнопки живёт в aria-label, и оттуда же его
              берёт подсказка при наведении (ui/shell/autoTitle.ts): title мы
              пишем руками только там, где он объясняет запрет. */}
          <div className={styles.actions}>
            <button
              type="button"
              className={`btn btn-sm ${styles.actionBtn} ${styles.iconBtn}`}
              aria-label={t('library.edit')}
              disabled={lockedByVariant}
              title={lockedByVariant ? lockedTitle : undefined}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
            >
              <EditIcon />
            </button>
            {/* Клонирование не трогает ни оригинал, ни варианты — доступно
                даже для блока из заблокированного варианта. */}
            <button
              type="button"
              className={`btn btn-sm ${styles.actionBtn} ${styles.iconBtn}`}
              aria-label={t('library.clone')}
              onClick={(e) => {
                e.stopPropagation();
                actions.addSegment(cloneSegment(segment));
              }}
            >
              <CloneIcon />
            </button>
            {/* Удаление насовсем: корзина вместо крестика и красная обводка
                btn-danger — единственная кнопка ряда, которую видно как
                опасную. Подтверждение (ConfirmDialog ниже) остаётся. */}
            <button
              type="button"
              className={`btn btn-sm btn-danger ${styles.actionBtn} ${styles.iconBtn}`}
              aria-label={t('library.delete')}
              disabled={lockedByVariant}
              title={lockedByVariant ? lockedTitle : undefined}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmOpen(true);
              }}
            >
              <DeleteIcon />
            </button>
            {/* Проверка цены ничего не меняет в поездке, поэтому она есть и у
                блока из заблокированного варианта — как и клонирование. */}
            {segment.kind === 'flight' && (
              <div onClick={(e) => e.stopPropagation()}>
                <PriceRefreshButton segments={[segment]} compact className={styles.actionBtn} />
              </div>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              <AddToVariantButton segmentId={segment.id} />
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title={t('library.delete.title')}
        message={confirmMessage}
        confirmLabel={t('library.delete')}
        danger
        onConfirm={() => {
          setConfirmOpen(false);
          actions.deleteSegment(segment.id);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <PriceHistoryModal
        segmentId={historyOpen ? segment.id : null}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
}
